<template xmlns="http://www.w3.org/1999/html">
  <loader-content :ref="(el: any) => loader = el">
  <div class="d-flex flex-column email-login" v-if="!state.emailIsSent">
    <div class="text-center">
      <h1>{{ $t('create_account')}}</h1>
    </div>
    <div class="d-flex flex-column gap-sm">
      <div class="">
        <label for="email" class="form-label text-capitalize-first">{{ $t('email') }}</label>
        <input v-model="state.email" id="email" type="email" class="form-control" :placeholder="$t('enter_email')">
      </div>
      <div class="">
        <label for="password" class="form-label text-capitalize-first">{{ $t('password') }}</label>
        <div class="password-input">
          <input v-model="state.password" id="password" :type="state.showPassword ? 'text' : 'password'" class="form-control" :placeholder="$t('enter_password')">
          <button type="button" class="password-toggle" @click.prevent="state.showPassword = !state.showPassword">
            <SvgIcon :icon="state.showPassword ? 'eye-off' : 'eye'"></SvgIcon>
          </button>
        </div>
      </div>
    </div>
    <div class="d-flex flex-column gap-sm">
      <label class="form-check d-flex align-items-start">
        <input v-model="state.termsAccepted" type="checkbox" class="form-check-input">
        <span>
          {{ $t('terms_notice_prefix') }}
          <router-link :to="{name: 'termsOfUse'}" target="_blank">{{ $t('terms_of_use_link') }}</router-link>
          and
          <router-link :to="{name: 'privacyPolicy'}" target="_blank">{{ $t('privacy_policy_link') }}</router-link>
        </span>
      </label>
    </div>
    <div>
      <button @click.prevent="register()" :disabled="!canSubmit" class="btn btn-primary w-100 btn-lg">{{$t('sign_up')}}</button>
    </div>
    <div class="text-center">
      <router-link :to="{name:'emailLogin'}"><span>{{$t('already_have_account')}}</span> <span class="register-here">{{$t('log_in')}}</span></router-link>
    </div>
  </div>
  <div v-else class="d-flex flex-column email-login">
    <div class="text-center">
      <h1>{{ $t('check_your_email')}}</h1>
    </div>
    <div class="text-center d-flex justify-content-center">
      <div class="mail-icon">
        <SvgIcon icon="mail"></SvgIcon>
      </div>
    </div>
    <div class="text-center">
      <span class="info">{{$t('confirmation_email_sent', {email: state.email})}}</span>
    </div>
    <div>
      <button @click.prevent="resend()" :disabled="state.resendCountdown > 0" class="btn btn-secondary w-100 btn-lg">
        {{ state.resendCountdown > 0 ? $t('resend_email_countdown', {seconds: state.resendCountdown}) : $t('resend_email') }}
      </button>
    </div>
  </div>
  </loader-content>
</template>
<script lang="ts" setup>
import {reactive} from "@vue/reactivity";
import {api} from "@/service/APIClient";
import {computed, onBeforeUnmount, ref} from "vue";
import {useNotification} from "@kyvg/vue3-notification";
import {$t} from "@/i18n";
import SvgIcon from "@/components/SvgIcon.vue";
import LoaderContent from "@/components/LoaderContent.vue";
import RegisterRequestDTO from "@/models/request/RegisterRequestDTO";

const RESEND_COOLDOWN_SECONDS = 60;

const state = reactive({
  email: '' as string,
  password: '' as string,
  showPassword: false as boolean,
  termsAccepted: false as boolean,
  emailIsSent: false as boolean,
  resendCountdown: 0 as number,
});

const canSubmit = computed(() => {
  return state.email.length > 0 && state.password.length >= 8 && state.termsAccepted;
})

const loader = ref<InstanceType<typeof LoaderContent> | null>(null)
const notification = useNotification()
let countdownInterval: number|any = null;

const startCountdown = () => {
  state.resendCountdown = RESEND_COOLDOWN_SECONDS;
  countdownInterval = setInterval(() => {
    state.resendCountdown--;
    if (state.resendCountdown <= 0) {
      clearInterval(countdownInterval);
    }
  }, 1000);
}

const register = async () => {
  if (!canSubmit.value) {
    return;
  }

  loader.value?.load(async () => {
    try {
      const result = await api.call.token.register({email: state.email, password: state.password} as RegisterRequestDTO)
      state.email = result.email
      state.emailIsSent = true
      notification.notify({type: 'success', title: $t('notifications.verification_email_sent'), duration: 1500})
      startCountdown()
    } catch (e: any) {
      if (e.response?.data?.[0]?.code === 'EMAIL_TAKEN') {
        notification.notify({type: 'error', title: $t('notifications.email_already_registered'), duration: 1500})
      } else {
        notification.notify({type: 'error', title: $t('notifications.registration_error'), duration: 1500})
      }
    }
  });
}

const resend = async () => {
  if (state.resendCountdown > 0) {
    return;
  }

  loader.value?.load(async () => {
    try {
      await api.call.token.resendVerification(state.email)
      notification.notify({type: 'success', title: $t('notifications.verification_email_sent'), duration: 1500})
    } catch (e) {}
    startCountdown()
  });
}

onBeforeUnmount(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
})

</script>
